/*
* Broadcast Theme
*
* Use this file to add custom Javascript to Broadcast. Keeping your custom
* Javascript in this file will make it easier to update Broadcast. In order
* to use this file you will need to open layout/theme.liquid and uncomment
* the custom.js script import line near the bottom of the file.
*/

(function () {
  'use strict';

  /* =========================
   * Tiny helpers
   * =======================*/
  function ready(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  function rafThrottle(fn) {
    var ticking = false;
    return function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        fn();
      });
    };
  }

  function onShopifySection(ev, fn) {
    document.addEventListener(ev, function (e) {
      fn(e && e.target ? e.target : null, e);
    });
  }

  /* =========================
   * 1) Mobile body class
   * =======================*/
  function initMobileClass() {
    if (window.matchMedia('(max-width: 749px)').matches) {
      document.body.classList.add('is-mobile');
    }
  }

  /* =========================
   * 2) Featured collection: reveal slide-in (padding -> 0)
   * =======================*/
  var carouselRevealObserver = null;

  function initCarouselReveal(scope) {
    var root = scope || document;
    var sections = root.querySelectorAll('.index-products[data-section-type="product-grid"]');
    if (!sections.length) return;

    if (!('IntersectionObserver' in window)) {
      sections.forEach(function (sec) {
        sec.classList.add('is-carousel-in-view');
      });
      return;
    }

    if (!carouselRevealObserver) {
      carouselRevealObserver = new IntersectionObserver(
        function (entries, obs) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-carousel-in-view');
            obs.unobserve(entry.target);
          });
        },
        { threshold: 0.8 }
      );
    }

    sections.forEach(function (sec) {
      // only observe if not already revealed
      if (!sec.classList.contains('is-carousel-in-view')) {
        carouselRevealObserver.observe(sec);
      }
    });
  }

  /* =========================
   * 3) Collection list progress bar
   * =======================*/
  function initCollectionSliderProgress(section) {
    var slider = section.querySelector('[data-grid-slider]');
    var bar = section.querySelector('[data-slider-progress-bar]');
    if (!slider || !bar) return;

    var update = rafThrottle(function () {
      var scrollWidth = slider.scrollWidth;
      var clientWidth = slider.clientWidth;
      var maxScroll = scrollWidth - clientWidth;

      if (maxScroll <= 0 || scrollWidth <= 0) {
        bar.style.width = '100%';
        return;
      }

      var base = clientWidth / scrollWidth;
      var extra = (slider.scrollLeft / maxScroll) * (1 - base);
      var progress = base + extra;

      bar.style.width = (progress * 100).toFixed(3) + '%';
    });

    update();
    slider.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
  }

  function initCollectionListProgressAll(scope) {
    (scope || document)
      .querySelectorAll('[data-section-type="list-collections"]')
      .forEach(initCollectionSliderProgress);
  }

  /* =========================
   * 4) Featured collection smooth drag + progress bars
   * =======================*/
  function findScrollTrack(root) {
    return (
      root.querySelector('[data-grid-slider]') ||
      root.querySelector('.grid.grid--mobile-slider') ||
      root.querySelector('.grid')
    );
  }

  function findHorizontalScroller(startEl, stopAt) {
    var el = startEl;
    while (el && el !== stopAt && el.nodeType === 1) {
      var cs = getComputedStyle(el);
      if (el.scrollWidth > el.clientWidth && cs.overflowX !== 'visible') return el;
      el = el.parentElement;
    }
    return startEl;
  }

  function initFeaturedSection(root) {
    if (!root || root.__featuredScrollInit) return;
    root.__featuredScrollInit = true;

    var track = findScrollTrack(root);
    if (!track) return;

    var scroller = findHorizontalScroller(track, root);
    var barWraps = root.querySelectorAll('.grid__progress');
    if (!barWraps.length) return;

    function setBarsWidth(pct) {
      barWraps.forEach(function (wrap) {
        var bar = wrap.querySelector('.grid__progress-bar');
        if (bar) bar.style.width = pct;
      });
    }

    var updateProgress = rafThrottle(function () {
      var scrollWidth = scroller.scrollWidth;
      var clientWidth = scroller.clientWidth;
      var maxScroll = scrollWidth - clientWidth;

      if (maxScroll <= 0 || scrollWidth <= 0) {
        setBarsWidth('100%');
        return;
      }

      var base = clientWidth / scrollWidth;
      var extra = (scroller.scrollLeft / maxScroll) * (1 - base);
      var progress = base + extra;

      setBarsWidth((progress * 100).toFixed(3) + '%');
    });

    function isInteractive(target) {
      return !!(target && target.closest("a, button, input, textarea, select, [role='button'], [data-clickable]"));
    }

    // Pointer/touch drag
    var dragging = false;
    var hasDragged = false;
    var sx = 0, sy = 0, sl = 0;
    var DRAG_THRESHOLD = 5;

    function dragStart(x, y) {
      dragging = true;
      hasDragged = false;
      sx = x;
      sy = y;
      sl = scroller.scrollLeft;
    }

    function dragMove(x, y, evt) {
      if (!dragging) return;

      var dx = x - sx;
      var dy = y - sy;

      if (!hasDragged) {
        if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
        if (Math.abs(dy) > Math.abs(dx)) {
          dragging = false;
          return;
        }
        hasDragged = true;
        scroller.classList.add('is-dragging');
      }

      scroller.scrollLeft = sl + (sx - x);
      updateProgress();

      if (evt && evt.cancelable) evt.preventDefault();
    }

    function dragEnd() {
      if (!dragging) return;
      dragging = false;
      if (!hasDragged) return;
      scroller.classList.remove('is-dragging');
    }

    function measureAndUpdate() {
      updateProgress();
    }

    // Bind scroll
    scroller.addEventListener('scroll', updateProgress, { passive: true });

    // Bind drag
    if (window.PointerEvent) {
      scroller.addEventListener(
        'pointerdown',
        function (e) {
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          if (isInteractive(e.target)) return;
          if (scroller.setPointerCapture) scroller.setPointerCapture(e.pointerId);
          dragStart(e.clientX, e.clientY);
        },
        { passive: false }
      );

      window.addEventListener(
        'pointermove',
        function (e) {
          if (!dragging) return;
          dragMove(e.clientX, e.clientY, e);
        },
        { passive: false }
      );

      window.addEventListener('pointerup', dragEnd, { passive: true });
      window.addEventListener('pointercancel', dragEnd, { passive: true });
    } else {
      scroller.addEventListener(
        'mousedown',
        function (e) {
          if (e.button !== 0) return;
          if (isInteractive(e.target)) return;
          dragStart(e.clientX, e.clientY);
        },
        { passive: false }
      );

      window.addEventListener(
        'mousemove',
        function (e) {
          if (!dragging) return;
          dragMove(e.clientX, e.clientY, e);
        },
        { passive: false }
      );

      window.addEventListener('mouseup', dragEnd, { passive: true });

      scroller.addEventListener(
        'touchstart',
        function (e) {
          var t = e.touches && e.touches[0];
          if (!t) return;
          if (isInteractive(e.target)) return;
          dragStart(t.clientX, t.clientY);
        },
        { passive: true }
      );

      scroller.addEventListener(
        'touchmove',
        function (e) {
          var t = e.touches && e.touches[0];
          if (!t) return;
          dragMove(t.clientX, t.clientY, e);
        },
        { passive: false }
      );

      scroller.addEventListener('touchend', dragEnd, { passive: true });
      scroller.addEventListener('touchcancel', dragEnd, { passive: true });
    }

    // Re-measure when images load
    track.querySelectorAll('img').forEach(function (img) {
      if (img.complete) return;
      img.addEventListener('load', measureAndUpdate, { passive: true });
    });

    window.addEventListener('resize', measureAndUpdate);
    setTimeout(measureAndUpdate, 0);
    measureAndUpdate();
  }

  function initFeaturedScrollbars(scope) {
    (scope || document).querySelectorAll('.index-products').forEach(initFeaturedSection);
  }

  /* =========================
   * 5) Global countdown timer
   * =======================*/
  var countdownInterval = null;

  function parseCountdownDate(input) {
    if (!input) return null;
    if (/^\d{4}-\d{2}-\d{2}$/.test(input)) return new Date(input + 'T00:00:00');
    var d = new Date(input);
    return isNaN(d.getTime()) ? null : d;
  }

  function formatCountdown(ms) {
    if (ms <= 0) return null;
    var s = Math.floor(ms / 1000);
    var d = Math.floor(s / 86400);
    var h = Math.floor((s % 86400) / 3600);
    var m = Math.floor((s % 3600) / 60);
    return d + ' days ' + h + ' hours ' + m + ' minutes';
  }

  function updateCountdowns() {
    document.querySelectorAll('[data-countdown-target]').forEach(function (el) {
      var t = parseCountdownDate(el.getAttribute('data-countdown-target'));
      var expired = el.getAttribute('data-countdown-expired') || "It's live!";
      if (!t) {
        el.textContent = '—';
        return;
      }
      var diff = t.getTime() - Date.now();
      el.textContent = formatCountdown(diff) || expired;
    });
  }

  function initCountdowns() {
    updateCountdowns();
    if (countdownInterval) clearInterval(countdownInterval);
    countdownInterval = setInterval(updateCountdowns, 60000);
  }

  /* =========================
   * 6) Image scroll (about page parallax)
   * =======================*/
  function initImageScrollSection(root) {
    var stage = root.querySelector('[data-image-stage]');
    var media = root.querySelector('[data-image-media]');
    var img = root.querySelector('.image-scroll__img');
    if (!stage || !media || !img) return;

    function vhToPx(vh) {
      return window.innerHeight * (vh / 100);
    }

    function readNumberVar(el, name) {
      var v = getComputedStyle(el).getPropertyValue(name).trim();
      return parseFloat(v.replace('vh', '').replace('px', '')) || 0;
    }

    var startVH = readNumberVar(root, '--parallax-start');
    var endVH = readNumberVar(root, '--parallax-end');

    if (!(window.gsap && window.ScrollTrigger)) return;

    if (root._imageScrollTween) {
      root._imageScrollTween.kill();
      root._imageScrollTween = null;
    }

    gsap.set(media, { y: vhToPx(startVH) });

    var tween = gsap.to(media, {
      y: vhToPx(endVH),
      ease: 'none',
      scrollTrigger: {
        trigger: root,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true,
        invalidateOnRefresh: true
      }
    });

    root._imageScrollTween = tween;

    // keep this listener lean; gsap refreshes are not frequent
    ScrollTrigger.addEventListener('refreshInit', function () {
      gsap.set(media, { y: vhToPx(startVH) });
      tween.vars.y = vhToPx(endVH);
    });
  }

  function initImageScrollAll(scope) {
    (scope || document).querySelectorAll('[data-image-scroll]').forEach(initImageScrollSection);
  }

  /* =========================
   * 7) Sidebar FAQ filtering
   * =======================*/
  class FAQFilteringHandler {
    constructor(scope) {
      var root = scope || document;

      this.faqButtons = Array.prototype.slice.call(root.querySelectorAll('[data-faq-heading]'));
      this.accordions = Array.prototype.slice.call(root.querySelectorAll('[data-faq-accordion]'));
      this.textBlocks = Array.prototype.slice.call(root.querySelectorAll('[data-faq-text]'));

      if (this.faqButtons.length) this._init();
    }

    _init() {
      this._setInitialState();

      this.faqButtons.forEach((btn) => {
        var group = btn.getAttribute('data-faq-heading');
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this._showGroup(group);
        });
      });
    }

    _setInitialState() {
      var fromHash = (location.hash || '').replace('#', '');
      var hashBtn = this.faqButtons.find((b) => b.getAttribute('data-faq-heading') === fromHash);
      var initialGroup = (hashBtn || this.faqButtons[0]).getAttribute('data-faq-heading');
      this._showGroup(initialGroup);
    }

    _showGroup(group) {
      this.faqButtons.forEach((btn) => {
        btn.classList.toggle('is-selected', btn.getAttribute('data-faq-heading') === group);
      });

      this.textBlocks.forEach((el) => {
        var on = el.getAttribute('data-faq-text') === group;
        el.classList.toggle('visible-text', on);
        el.classList.toggle('hidden-text', !on);
      });

      this.accordions.forEach((el) => {
        var on = el.getAttribute('data-faq-accordion') === group;
        el.classList.toggle('visible-accordion', on);
        el.classList.toggle('hidden-accordion', !on);
        if (!on && el.hasAttribute('open')) el.removeAttribute('open');
      });
    }
  }

  function initializeScripts(scope) {
    new FAQFilteringHandler(scope);
    if (typeof initializeSwipers === 'function') initializeSwipers();
  }

  /* =========================
   * 8) Conditional jQuery loader (only if needed)
   * =======================*/
  function needsJQueryFeatures() {
    return (
      document.querySelector('.jdgm-rev-widg__summary-stars, .jdgm-rev-widg__summary-text') ||
      document.querySelector('.rich-text-slider')
    );
  }

  function loadJQuery(callback) {
    if (window.jQuery) return callback();

    var script = document.createElement('script');
    script.src = 'https://code.jquery.com/jquery-3.6.0.min.js';
    script.onload = callback;
    document.head.appendChild(script);
  }

  function initJQueryEnhancements() {
    loadJQuery(function () {
      // 0ms defer to ensure Judge.me/Flickity DOM is present
      setTimeout(function () {
        var $ = window.jQuery;
        if (!$) return;

        /* Judge.me review formatting */
        var averageReview = document
          .querySelector('.jdgm-rev-widg__summary-stars, .jdgm-rev-widg__summary-text')
          ?.getAttribute('aria-label');

        if (averageReview) {
          averageReview = averageReview.split('is ').pop().split(' stars').shift();

          $('.jdgm-rev-widg__summary-stars').wrap('<div class="review-section-inner"></div>');
          $('.jdgm-rev-widg__summary-text').wrap('<div class="review-section-text"></div>');
          $('.review-section-inner').wrap('<div class="review-section"></div>');

          $('.review-section-inner').prepend('<span class="average-review">' + averageReview + '</span>');
          $('.review-section-text').prepend('<span class="average-review-title">Average Rating</span>');

          $('.jdgm-rev__rating').each(function () {
            var content = $(this).closest('.jdgm-rev').find('.jdgm-rev__content');
            $(this).prependTo(content);
          });
        }

        /* Rich text slider: controls + counter per slide */
        $('.rich-text-slider').each(function () {
          var $section = $(this);
          if ($section.data('rtSliderEnhanced')) return;
          $section.data('rtSliderEnhanced', true);

          var $slider = $section.find('[data-slider]');
          if (!$slider.length) return;

          var $slides = $slider.find('.slideshow__slide');
          if (!$slides.length) return;

          var $origPrev = $slider.children('.flickity-prev-next-button.previous').first();
          var $origNext = $slider.children('.flickity-prev-next-button.next').first();
          if (!$origPrev.length || !$origNext.length) return;

          var totalSlides = $slides.length;
          var counters = [];

          function updateCountersFromSelected() {
            var selectedIndex = $slides.index($slides.filter('.is-selected'));
            if (selectedIndex < 0) selectedIndex = 0;
            var current = selectedIndex + 1;

            counters.forEach(function ($c) {
              $c.text(current + ' of ' + totalSlides);
            });
          }

          $slides.each(function () {
            var $slide = $(this);
            var $sub = $slide.find('h6.rich-text-slide__subheading').first();
            if (!$sub.length) return;

            var $row = $('<div class="rich-text-slider__heading-row"></div>');
            $sub.after($row);
            $row.append($sub);

            var $controls = $('<div class="rich-text-slider__controls"></div>');

            var $prevClone = $origPrev.clone(false).removeAttr('style');
            var $nextClone = $origNext.clone(false).removeAttr('style');

            $prevClone.on('click', function (e) {
              e.preventDefault();
              $origPrev.trigger('click');
            });

            $nextClone.on('click', function (e) {
              e.preventDefault();
              $origNext.trigger('click');
            });

            var $counter = $('<h6 class="rich-text-slider__counter"></h6>');
            counters.push($counter);

            $controls.append($prevClone, $counter, $nextClone);
            $row.append($controls);
          });

          updateCountersFromSelected();

          if (window.MutationObserver) {
            $slides.each(function () {
              var slideEl = this;
              var observer = new MutationObserver(function (mutations) {
                for (var i = 0; i < mutations.length; i++) {
                  var m = mutations[i];
                  if (m.type === 'attributes' && m.attributeName === 'class') {
                    updateCountersFromSelected();
                    break;
                  }
                }
              });

              observer.observe(slideEl, { attributes: true, attributeFilter: ['class'] });
            });
          }
        });
      }, 0);
    });
  }

  /* =========================
   * 9) Homepage: reveal header left bar + mobile logo
   * =======================*/
  function initHeaderLeftBarReveal() {
    if (!document.body.classList.contains('template-index')) return;

    var leftBar = document.querySelector('.header__desktop__bar__l');
    var heroSection = document.querySelector('.index-hero');
    if (!leftBar || !heroSection) return;

    if (leftBar.dataset.leftBarInit === 'true') return;
    leftBar.dataset.leftBarInit = 'true';

    leftBar.style.display = 'none';
    var isVisible = false;
    var hideTimer = null;

    function showLeftBar() {
      if (isVisible) return;
      isVisible = true;

      if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }

      leftBar.style.display = 'block';
      requestAnimationFrame(function () {
        leftBar.classList.add('header-leftbar--visible');
      });
    }

    function hideLeftBar() {
      if (!isVisible) return;
      isVisible = false;

      leftBar.classList.remove('header-leftbar--visible');

      if (hideTimer) clearTimeout(hideTimer);
      hideTimer = setTimeout(function () {
        if (!isVisible) leftBar.style.display = 'none';
      }, 200);
    }

    var handle = rafThrottle(function () {
      var rect = heroSection.getBoundingClientRect();
      if (rect.bottom <= 0) showLeftBar();
      else hideLeftBar();
    });

    window.addEventListener('scroll', handle, { passive: true });
    window.addEventListener('resize', handle);
    handle();
  }

  function initHeaderMobileLogoReveal() {
    if (!document.body.classList.contains('template-index')) return;
    if (!window.matchMedia('(max-width: 989px)').matches) return;

    var mobileLogo = document.querySelector('.header__logo--image');
    var heroSection = document.querySelector('.index-hero');
    if (!mobileLogo || !heroSection) return;

    if (mobileLogo.dataset.mobileLogoInit === 'true') return;
    mobileLogo.dataset.mobileLogoInit = 'true';

    var isVisible = false;

    function show() {
      if (isVisible) return;
      isVisible = true;
      mobileLogo.classList.add('header-mobilelogo--visible');
    }

    function hide() {
      if (!isVisible) return;
      isVisible = false;
      mobileLogo.classList.remove('header-mobilelogo--visible');
    }

    var handle = rafThrottle(function () {
      var rect = heroSection.getBoundingClientRect();
      if (rect.bottom <= 0) show();
      else hide();
    });

    window.addEventListener('scroll', handle, { passive: true });
    window.addEventListener('resize', handle);
    handle();
  }

  /* =========================
   * 10) Header dropdown buttons (+ click outside close)
   * =======================*/
  var dropdownOutsideBound = false;

  function initHeaderDropdownButtons(scope) {
    var root = scope || document;
    var buttons = root.querySelectorAll('.menu__item[data-has-dropdown] .menu__toggle');

    buttons.forEach(function (btn) {
      if (btn.dataset.dropdownBound === 'true') return;
      btn.dataset.dropdownBound = 'true';

      btn.addEventListener('click', function (event) {
        event.preventDefault();

        var item = btn.closest('.menu__item[data-has-dropdown]');
        if (!item) return;

        var isOpen = item.classList.toggle('is-open');
        btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

        if (isOpen) {
          document.body.classList.add('meganav--visible');
        } else if (!document.querySelector('.menu__item[data-has-dropdown].is-open')) {
          document.body.classList.remove('meganav--visible');
        }
      });
    });

    if (!dropdownOutsideBound) {
      dropdownOutsideBound = true;
      document.addEventListener('click', function (e) {
        if (e.target.closest('.menu__item[data-has-dropdown]')) return;

        document.querySelectorAll('.menu__item[data-has-dropdown].is-open').forEach(function (openItem) {
          openItem.classList.remove('is-open');
          var toggle = openItem.querySelector('.menu__toggle');
          if (toggle) toggle.setAttribute('aria-expanded', 'false');
        });

        document.body.classList.remove('meganav--visible');
      });
    }
  }

  /* =========================
   * 11) Rich text slider: equal height cards
   * =======================*/
  var richTextResizeBound = false;

  function equalizeRichTextSliderHeights(scope) {
    var root = scope || document;
    var sliders = root.querySelectorAll('.rich-text-slider');
    if (!sliders.length) return;

    sliders.forEach(function (section) {
      var cards = section.querySelectorAll('.rich-text-slide__card');
      if (!cards.length) return;

      cards.forEach(function (card) {
        card.style.minHeight = '';
      });

      var maxHeight = 0;
      cards.forEach(function (card) {
        var h = card.offsetHeight;
        if (h > maxHeight) maxHeight = h;
      });

      section.style.setProperty('--rt-slide-card-height', maxHeight + 'px');
    });
  }

  function initRichTextSliderHeights() {
    equalizeRichTextSliderHeights();

    if (!richTextResizeBound) {
      richTextResizeBound = true;
      window.addEventListener(
        'resize',
        rafThrottle(function () {
          equalizeRichTextSliderHeights();
        })
      );
    }
  }

  /* =========================
   * 12) Inline search
   * =======================*/
  function initInlineSearch(scope) {
    var root = scope || document;
    var wraps = root.querySelectorAll('.header__search-inline');
    if (!wraps.length) return;

    wraps.forEach(function (wrap) {
      var toggleBtn = wrap.querySelector('.search-toggle');
      var form = wrap.querySelector('.search-inline-form');
      if (!toggleBtn || !form) return;

      if (toggleBtn.dataset.inlineSearchBound === 'true') return;
      toggleBtn.dataset.inlineSearchBound = 'true';

      toggleBtn.addEventListener('click', function () {
        var isOpen = wrap.classList.toggle('is-open');
        toggleBtn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

        if (isOpen) {
          var input = form.querySelector('input');
          if (input) input.focus();
        }
      });
    });
  }

  /* =========================
   * 13) PDP image collapse (delegated)
   * =======================*/
  function initPdpImageCollapse() {
    if (document.__pdpMediaCollapseBound) return;
    document.__pdpMediaCollapseBound = true;

    document.addEventListener('click', function (event) {
      var btn = event.target.closest('[data-more-media-toggle]');
      if (!btn) return;

      var container = btn.closest('product-info') || document;
      var extraMedia = container.querySelectorAll('[data-extra-media].media-hidden');
      var batchSize = 4;

      for (var i = 0; i < extraMedia.length && i < batchSize; i++) {
        extraMedia[i].classList.remove('media-hidden');
      }

      if (container.querySelectorAll('[data-extra-media].media-hidden').length === 0) {
        btn.remove();
      }
    });
  }

  /* =========================
   * 14) Homepage: move title on mobile
   * =======================*/
  function initMoveTitleOnMobile() {
    if (!document.body.classList.contains('template-index')) return;

    var productPage = document.querySelector('.product__page');
    var productImages = productPage && productPage.querySelector('product-images');
    var headingBlock =
      productPage && productPage.querySelector('.product__content .product__block.product__text');
    if (!productPage || !productImages || !headingBlock) return;

    var mobile = window.matchMedia('(max-width: 749px)');

    function repositionHeading(e) {
      if (e.matches) {
        productPage.insertBefore(headingBlock, productImages);
      } else {
        var productContent = productPage.querySelector('.product__content .form__width');
        if (productContent && !productContent.contains(headingBlock)) {
          productContent.insertBefore(headingBlock, productContent.firstChild);
        }
      }
    }

    repositionHeading(mobile);

    // avoid double-binding if called again
    if (!headingBlock.dataset.moveTitleBound) {
      headingBlock.dataset.moveTitleBound = 'true';
      mobile.addEventListener('change', repositionHeading);
    }
  }

  /* =========================
   * 15) Global cart drawer toggle (capture)
   * =======================*/
  function initCartDrawerToggle() {
    if (document.__cartDrawerToggleBound) return;
    document.__cartDrawerToggleBound = true;

    document.addEventListener(
      'click',
      function (e) {
        var toggle = e.target.closest('[data-cart-toggle]');
        if (!toggle) return;

        if (window.theme && window.theme.settings && window.theme.settings.cartType !== 'drawer') return;

        var cartDrawer = document.querySelector('cart-drawer');
        if (!cartDrawer) return;

        e.preventDefault();
        cartDrawer.dispatchEvent(new CustomEvent('theme:cart-drawer:show'));

        window.a11y = window.a11y || {};
        window.a11y.lastElement = toggle;
      },
      true
    );
  }

  /* =========================
   * 16) Timeless view: swap panels by hero buttons
   * =======================*/
  function initTimelessPanelSwap() {
  if (!window.location.pathname.includes('/timeless')) return;

  var btnGroup = document.querySelector('.hero__button-group');
  if (!btnGroup) return;

  var buttons = Array.from(btnGroup.querySelectorAll('a.btn'));
  if (!buttons.length) return;

  var faqsSection = document.querySelector('[id*="__section_accordion_group_"]');
  var shippingReturnsSection = document.querySelector('[id*="__section_sidebar_"]');
  var warrantySection = document.querySelector('[id*="__section_rich_text_columns_"]');

  var panels = [
    { key: 'faqs', el: faqsSection },
    { key: 'shipping-returns', el: shippingReturnsSection },
    { key: 'warranty', el: warrantySection }
  ].filter(function (p) {
    return p.el;
  });

  if (!panels.length) return;

  if (document.body.dataset.timelessSwapInit === 'true') return;
  document.body.dataset.timelessSwapInit = 'true';

  panels.forEach(function (p) {
    p.el.classList.add('ep-swap-panel');
  });

  function setActive(key) {
    panels.forEach(function (p) {
      p.el.classList.toggle('is-active', p.key === key);
    });

    buttons.forEach(function (b) {
      b.classList.remove('is-active');
    });

    var index = panels.findIndex(function (p) {
      return p.key === key;
    });

    if (index >= 0 && buttons[index]) buttons[index].classList.add('is-active');
  }
  
  function keyFromHash(hash) {
    if (!hash) return null;
    hash = hash.replace('#', '').toLowerCase();

    if (hash === 'shipping' || hash === 'returns' || hash === 'shipping-returns') return 'shipping-returns';
    if (hash === 'warranty') return 'warranty';
    if (hash === 'faqs' || hash === 'faq') return 'faqs';

    return null;
  }

  function applyHashSelection() {
    var key = keyFromHash(window.location.hash);
    if (key) setActive(key);
  }

 
  applyHashSelection();

  
  window.addEventListener('hashchange', applyHashSelection);

  document.addEventListener(
    'click',
    function (e) {
      var a = e.target.closest('a');
      if (!a) return;

      var href = a.getAttribute('href') || '';
      if (href.indexOf('#') === -1) return;

      var parts = href.split('#');
      var beforeHash = parts[0];      
      var hash = '#' + (parts[1] || '');

      
      var isSamePageHash = beforeHash === '' || beforeHash === window.location.pathname;
      var isTimelessLink = beforeHash.indexOf('/timeless-faq') !== -1;

      if (!(isSamePageHash || isTimelessLink)) return;

      var key = keyFromHash(hash);
      if (!key) return;


      e.preventDefault();


      if (history && history.pushState) {
        history.pushState(null, '', hash);
      } else {
        window.location.hash = hash;
      }

      
      setActive(key);
    },
    true
  );

  buttons.forEach(function (btn, i) {
    var panel = panels[i];
    if (!panel) return;

    btn.addEventListener('click', function (e) {
      e.preventDefault();
      setActive(panel.key);

    });
  });

    if (!keyFromHash(window.location.hash)) {
    setActive(panels[0].key);
  }
}

     /* =========================
   * 17) Timeless view: rich-text button -> rich-text swap (3 texts)
   * =======================*/
  function initTimelessRichTextSwap(scope) {
    if (!window.location.pathname.includes('/ep-timeless')) return;

    var root = scope || document;

    var candidates = Array.from(root.querySelectorAll('[data-section-type="rich-text"]')).filter(function (section) {
      return (
        section.querySelector('.hero__button-group a.btn') &&
        section.querySelectorAll('.hero__rte.body-medium').length >= 3
      );
    });

    if (!candidates.length) return;

    candidates.forEach(function (section) {
      // Avoid double init (theme editor / section reload)
      if (section.dataset.timelessRichTextSwapInit === 'true') return;
      section.dataset.timelessRichTextSwapInit = 'true';

      var btnGroup = section.querySelector('.hero__button-group');
      var buttons = Array.from(btnGroup.querySelectorAll('a.btn')).slice(0, 3);

      var texts = Array.from(section.querySelectorAll('.hero__rte.body-medium')).slice(0, 3);

      texts.forEach(function (t) {
      t.classList.add('ep-swap-panel', 'timeless-home');

      });

      function setActive(index) {
        texts.forEach(function (t, i) {
          t.classList.toggle('is-active', i === index);
        });

        buttons.forEach(function (b, i) {
          b.classList.toggle('is-active', i === index);
        });
      }

      buttons.forEach(function (btn, i) {
        btn.addEventListener('click', function (e) {
          e.preventDefault();
          setActive(i);
        });
      });

      setActive(0);
    });
  }

    /* =========================
   * 18) Ep-timeless PDP: require checkbox to enable ATC
   * =======================*/
 function initEpTimelessMadeToOrderAck(scope) {
  var root = scope || document;

  var wraps = root.querySelectorAll('[data-made-to-order-ack]');
  if (!wraps.length) return;

  wraps.forEach(function (wrap) {
    if (wrap.dataset.madeToOrderInit === 'true') return;
    wrap.dataset.madeToOrderInit = 'true';

    var form = wrap.closest('form.form') || wrap.closest('form');
    if (!form) return;

    var cb = wrap.querySelector('[data-made-to-order-checkbox]');
    var atc = form.querySelector('[data-add-to-cart]');
    var variantIdInput = form.querySelector('.product-variant-id');

    if (!cb || !atc) return;

    // Force initial disabled state (first paint)
    atc.disabled = true;
    atc.setAttribute('aria-disabled', 'true');

    function sync() {
      var variantDisabled = variantIdInput && variantIdInput.disabled;
      if (variantDisabled) {
        atc.disabled = true;
        atc.setAttribute('aria-disabled', 'true');
        return;
      }

      var enable = !!cb.checked;
      atc.disabled = !enable;

      if (enable) atc.removeAttribute('aria-disabled');
      else atc.setAttribute('aria-disabled', 'true');
    }

    function lockAfterThemeUpdates() {
      cb.checked = false; // require re-ack on size change
      sync();
      setTimeout(sync, 0);
      requestAnimationFrame(sync);
    }

    // Initial state
    sync();

    cb.addEventListener('change', sync);

    // Variant changes (ring size)
    form.addEventListener('change', function (e) {
      if (!e.target || e.target.name !== 'id') return;
      lockAfterThemeUpdates();
    });

    // Broadcast/theme variant event
    form.addEventListener('variant:change', function () {
      lockAfterThemeUpdates();
    });
  });
}

    /* =========================
   * 19) cart drawer drag
   * =======================*/

function initCartWidgetDrag(scope) {
  var root = scope || document;
  var sliders = root.querySelectorAll('.cart__widget__products');
  if (!sliders.length) return;

  function isInteractive(target) {
    return !!(target && target.closest("a, button, input, textarea, select, label, [role='button'], [data-clickable]"));
  }

  sliders.forEach(function (slider) {
    if (slider.dataset.cartWidgetDragInit === 'true') return;
    slider.dataset.cartWidgetDragInit = 'true';

    var dragging = false;
    var hasDragged = false;
    var startX = 0;
    var scrollLeft = 0;
    var DRAG_THRESHOLD = 5;

    function dragStart(x) {
      dragging = true;
      hasDragged = false;
      startX = x;
      scrollLeft = slider.scrollLeft;
    }

    function dragMove(x, evt) {
      if (!dragging) return;

      var dx = x - startX;

      if (!hasDragged) {
        if (Math.abs(dx) < DRAG_THRESHOLD) return;
        hasDragged = true;
        slider.classList.add('is-dragging');
      }

      slider.scrollLeft = scrollLeft - dx;

      if (evt && evt.cancelable) evt.preventDefault();
    }

    function dragEnd() {
      if (!dragging) return;
      dragging = false;
      slider.classList.remove('is-dragging');
    }

    if (window.PointerEvent) {
      slider.addEventListener(
        'pointerdown',
        function (e) {
          if (e.pointerType === 'mouse' && e.button !== 0) return;
          if (isInteractive(e.target)) return;
          if (slider.setPointerCapture) slider.setPointerCapture(e.pointerId);
          dragStart(e.clientX);
        },
        { passive: false }
      );

      window.addEventListener(
        'pointermove',
        function (e) {
          if (!dragging) return;
          dragMove(e.clientX, e);
        },
        { passive: false }
      );

      window.addEventListener('pointerup', dragEnd, { passive: true });
      window.addEventListener('pointercancel', dragEnd, { passive: true });
    } else {
      slider.addEventListener(
        'mousedown',
        function (e) {
          if (e.button !== 0) return;
          if (isInteractive(e.target)) return;
          dragStart(e.clientX);
        },
        { passive: false }
      );

      window.addEventListener(
        'mousemove',
        function (e) {
          if (!dragging) return;
          dragMove(e.clientX, e);
        },
        { passive: false }
      );

      window.addEventListener('mouseup', dragEnd, { passive: true });

      slider.addEventListener(
        'touchstart',
        function (e) {
          var t = e.touches && e.touches[0];
          if (!t) return;
          if (isInteractive(e.target)) return;
          dragStart(t.clientX);
        },
        { passive: true }
      );

      slider.addEventListener(
        'touchmove',
        function (e) {
          var t = e.touches && e.touches[0];
          if (!t) return;
          dragMove(t.clientX, e);
        },
        { passive: false }
      );

      slider.addEventListener('touchend', dragEnd, { passive: true });
      slider.addEventListener('touchcancel', dragEnd, { passive: true });
    }
  });
}

  /* =========================
   * Boot
   * =======================*/
  function boot(scope) {
    initMobileClass();

    initCarouselReveal(scope);
    initCollectionListProgressAll(scope);
    initFeaturedScrollbars(scope);

    initCountdowns();

    initImageScrollAll(scope);

    // Delayed
    setTimeout(function () {
      initializeScripts(scope);
    }, 500);

    initHeaderLeftBarReveal();
    initHeaderMobileLogoReveal();

    initHeaderDropdownButtons(scope);

    initRichTextSliderHeights();

    initInlineSearch(scope);
    initPdpImageCollapse();
    
    initEpTimelessMadeToOrderAck(scope);

    initMoveTitleOnMobile();

    initCartDrawerToggle();
    initCartWidgetDrag(scope);

    initTimelessPanelSwap();
    initTimelessRichTextSwap(scope);

    if (needsJQueryFeatures()) {
      initJQueryEnhancements();
    }
  }

  ready(function () {
    boot(document);
  });

  // Theme editor hooks (keep light + scoped where possible)
  onShopifySection('shopify:section:load', function (target) {
    boot(target || document);
  });

  onShopifySection('shopify:section:unload', function (target) {
    // kill GSAP tween if we can find a child data-image-scroll
    if (!target) return;
    var el = target.querySelector('[data-image-scroll]');
    if (el && el._imageScrollTween) {
      el._imageScrollTween.kill();
      el._imageScrollTween = null;
    }
  });

  // Featured scrollbars sometimes need to re-init on editor select/deselect
  document.addEventListener('shopify:section:select', function () {
    initFeaturedScrollbars(document);
  });
  document.addEventListener('shopify:section:deselect', function () {
    initFeaturedScrollbars(document);
  });
})();
